import { gql, ApolloQueryResult, ApolloLink } from '@apollo/client'
import _map from 'lodash/map'
import { apolloClientFactory } from './apolloClientFactory'
import { IGraphqlUserSchema } from '../../../types/user.types'

interface IUserInviteResponse {
  [inviteeAlias: string]: {
    email: string;
  };
}

enum BulkUserInviteStatusType {
  NO_BULK_USER_INVITE_RUNNING ='NoBulkUserInviteRunning',
  RUNNING_BULK_USER_INVITE = 'RunningBulkUserInvite'
}

interface IBulkUserInviteResult {
  startedAt: string;
  totalToInvite: number;
  successful: number;
  alreadyInvitedInLastDay: number;
  notEligible: number;
  failed: number;
}

interface IBulkUserInviteStatusResponse {
  bulkUserInviteEmailsStatus: {
    finishedAt: string | null;
    lastResult?: null | IBulkUserInviteResult;
    status?: null | IBulkUserInviteResult;
    __typename: BulkUserInviteStatusType;
  }
}

interface IBulkUserInviteResponse {
  sendBulkUserInviteEmails: {
    status?: null | IBulkUserInviteResult;
  }
}

interface ISendUserInviteResponse {
  sendUserInviteEmail: {
    globalUserId: string;
    __typename: string;
  }
}

interface IUserCreateResponse {
  createUser: IGraphqlUserSchema
}

interface IUserCreateObject {
  email: string;
  firstname: string;
  lastname: string;
  languageId: string;
}

const userProperties = [
  'firstname',
  'lastname',
  'languageId',
  'countryId',
  'gender',
  'company',
  'location',
  'position',
  'department',
  'about',
  'isTechnicalUser',
  'globalUserId',
  'dateDeleted',
  'email'
]

export function inviteUserCreationGraphQlApiFactory(apolloLink: ApolloLink) {
  const graphqlClient = apolloClientFactory(apolloLink)

  return {
    async inviteUserWithEmail(recipients: string[], personalMessage: string): Promise<string[]> {
      const mutation = gql`
        mutation inviteUserWithEmail {
          ${recipients.map((email, index) => `
            invitee${index}: inviteUserWithEmail(input: { email: "${email}", personalMessage: "${personalMessage}" }) { email }
          `)}
        }
      `

      const response = await graphqlClient.mutate({
        mutation
      }) as ApolloQueryResult<IUserInviteResponse>
      return _map(response.data, ({ email }) => email)
    },
    async createUser(user: IUserCreateObject) {
      const mutation = gql`
        mutation createUser($createUserInput: CreateUserInput!) {
          createUser(input: $createUserInput) {
            ${userProperties.join(' ')}
          }
        }
      `

      const response = await graphqlClient.mutate({
        mutation,
        variables: {
          createUserInput: {
            email: user.email,
            firstname: user.firstname,
            lastname: user.lastname,
            languageId: user.languageId
          }
        }
      }) as ApolloQueryResult<IUserCreateResponse>

      return response.data.createUser.globalUserId
    },
    async sendUserInviteEmail(globalUserId: string, testEmail = false) {
      const mutation = gql`
        mutation sendUserInviteEmail($globalUserId: UUID!, $testEmail: Boolean) {
          sendUserInviteEmail(globalUserId: $globalUserId, testEmail: $testEmail) {
            globalUserId
          }
        }
      `

      const response = await graphqlClient.mutate({
        mutation,
        variables: {
          globalUserId,
          testEmail
        }
      }) as ApolloQueryResult<ISendUserInviteResponse>

      return response.data.sendUserInviteEmail.globalUserId
    },
    async sendBulkUserInviteEmails() {
      let mutation = gql`
        mutation {
          bulkUserInviteEmailsStatus {
            __typename
            ... on RunningBulkUserInvite {
              status {
                startedAt
                totalToInvite
                successful
                alreadyInvitedInLastDay
                notEligible
                failed
              }
            }
            ... on NoBulkUserInviteRunning {
              lastResult {
                startedAt
                totalToInvite
                successful
                alreadyInvitedInLastDay
                notEligible
                failed
              }
              finishedAt
            }
          }
        }
      `

      const response = await graphqlClient.mutate({
        mutation
      }) as ApolloQueryResult<IBulkUserInviteStatusResponse>

      if (response.data.bulkUserInviteEmailsStatus.__typename === BulkUserInviteStatusType.NO_BULK_USER_INVITE_RUNNING) {
        mutation = gql`
          mutation {
            sendBulkUserInviteEmails {
              status {
                startedAt
                totalToInvite
                successful
                alreadyInvitedInLastDay
                notEligible
                failed
              }
            }
          }
        `
        const inviteResponse = await graphqlClient.mutate({
          mutation
        }) as ApolloQueryResult<IBulkUserInviteResponse>

        return inviteResponse.data.sendBulkUserInviteEmails.status?.totalToInvite
      }
      return response.data.bulkUserInviteEmailsStatus.lastResult?.totalToInvite
    }
  }
}
